import { useApolloClient, useMutation } from "@apollo/client/react";
import { CircleCheck, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { useFragment as getFragmentData } from "@lytra/frontend-common/__generated__";
import type { FuerDichEntryFieldsFragment } from "@lytra/frontend-common/__generated__/graphql";
import { Alert } from "@lytra/frontend-common/components/v2/ui/alert";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { EmptyState } from "@lytra/frontend-common/components/v2/ui/empty-state";
import { List } from "@lytra/frontend-common/components/v2/ui/list-item";
import { formatCount } from "@lytra/frontend-common/components/v2/ui/nav";
import { Skeleton } from "@lytra/frontend-common/components/v2/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@lytra/frontend-common/components/v2/ui/tabs";
import { TooltipProvider } from "@lytra/frontend-common/components/v2/ui/tooltip";
import { Badge } from "@lytra/frontend-common/components/v2/ui/badge";
import {
  FuerDichEntryRow,
  type FuerDichTab,
} from "@/components/notifications/FuerDichEntryRow";
import { NotificationKind } from "@lytra/frontend-common/__generated__/graphql";
import {
  FUER_DICH_ENTRIES,
  FUER_DICH_ENTRY_FIELDS,
  FUER_DICH_CONFIRM_REQUEST,
  FUER_DICH_MARK_DONE,
  FUER_DICH_MARK_READ,
  FUER_DICH_OPENED,
  FUER_DICH_REOPEN,
} from "@/components/notifications/notificationQueries";

const PAGE_SIZE = 50;
const SKELETON_ROWS = 4;

type FuerDichEntry = FuerDichEntryFieldsFragment;

/** What makes an entry look different to the reader. */
const signature = (entries: FuerDichEntry[]) =>
  entries
    .map((e) => `${e.id}|${e.lastEventAt}|${e.closedAt}|${e.readAt}`)
    .join("\n");

/**
 * Whether the server's first page shows something the snapshot does not. A snapshot that was
 * paged further, or lost rows to the user's own actions, is compared on the first page's
 * worth of rows only.
 */
function differsFromSnapshot(
  fresh: FuerDichEntry[],
  shown: FuerDichEntry[],
): boolean {
  const against = fresh.length < PAGE_SIZE ? shown : shown.slice(0, PAGE_SIZE);
  return signature(fresh) !== signature(against);
}

export interface FuerDichListViewProps {
  tab: FuerDichTab;
  onTabChange: (tab: FuerDichTab) => void;
  /** The bell's number, so the tab and the bell never disagree. */
  openCount: number | null;
  entries: FuerDichEntry[];
  status: "loading" | "error" | "ready";
  onRetry: () => void;
  onSelect: (
    entry: FuerDichEntry,
    event: React.MouseEvent<HTMLElement>,
  ) => void;
  onDone?: (entry: FuerDichEntry) => void;
  onConfirm?: (entry: FuerDichEntry) => void;
  onReopen?: (entry: FuerDichEntry) => void;
  /** The server list changed since the snapshot: offer to load it (D-16). */
  hasNewEntries?: boolean;
  onShowNew?: () => void;
  /** A refresh failed while rows are shown: the rows may be out of date. */
  stale?: boolean;
  /** The last page was full, so there may be more. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Id of the header, for the popover's `aria-labelledby`. */
  titleId?: string;
}

/** Four rows with the entry row's geometry; the region says it is busy, not each bar. */
function LoadingRows() {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" className="divide-y divide-divider">
      <span role="status" className="sr-only">
        {t("notifications.loading")}
      </span>
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <div
          key={index}
          data-slot="skeleton-row"
          className="flex min-h-10 items-start gap-3 px-4 py-2"
        >
          <Skeleton shape="circle" className="mt-0.5 size-4" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Skeleton shape="line" className="w-2/3" />
            <Skeleton shape="line" className="w-1/2" />
            <Skeleton shape="line" className="w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface Snapshot {
  status: "loading" | "error" | "ready";
  entries: FuerDichEntry[];
  /** Whether the last page loaded was a full one. */
  hasMore: boolean;
}

const LOADING: Snapshot = { status: "loading", entries: [], hasMore: false };

/**
 * The popover body: header, the Offen / Erledigt tabs and the list or its loading, empty and
 * error state. Purely presentational; the container owns the data (and with it the snapshot
 * rule: rows only ever change when the user loaded the list).
 */
export function FuerDichListView({
  tab,
  onTabChange,
  openCount,
  entries,
  status,
  onRetry,
  onSelect,
  onDone,
  onConfirm,
  onReopen,
  hasNewEntries = false,
  onShowNew,
  stale = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  titleId,
}: FuerDichListViewProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  let body: React.ReactNode;
  if (status === "loading") {
    body = <LoadingRows />;
  } else if (status === "error") {
    body = (
      <EmptyState
        variant="error"
        size="inline"
        title={t("notifications.error.title")}
        description={t("notifications.error.body")}
        actions={
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("notifications.error.retry")}
          </Button>
        }
      />
    );
  } else if (entries.length === 0) {
    body =
      tab === "open" ? (
        <EmptyState
          size="inline"
          icon={CircleCheck}
          title={t("notifications.empty.openTitle")}
          description={t("notifications.empty.openBody")}
        />
      ) : (
        <EmptyState
          size="inline"
          title={t("notifications.empty.closedTitle")}
          description={t("notifications.empty.closedBody")}
        />
      );
  } else {
    body = (
      <>
        <List>
          {entries.map((entry) => (
            <FuerDichEntryRow
              key={entry.id}
              entry={entry}
              tab={tab}
              onSelect={onSelect}
              onDone={onDone}
              onConfirm={onConfirm}
              onReopen={onReopen}
            />
          ))}
        </List>
        {hasMore && (
          // No infinite scroll: it would move content under the reader.
          <div className="px-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              loading={loadingMore}
              onClick={onLoadMore}
            >
              {t("notifications.loadMore")}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex flex-col">
        <h2 id={titleId} className="px-4 pt-4 text-heading-sm text-ink">
          {t("notifications.title")}
        </h2>
        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (value === "open" || value === "closed") onTabChange(value);
          }}
        >
          <div className="px-4 pt-2">
            <TabsList>
              <TabsTrigger value="open">
                {t("notifications.tabs.open")}
                {openCount != null && openCount > 0 && (
                  <>
                    {/* Word break for the accessible name ("Offen 3"). TabsTrigger's own
                        `count` prints the plain number, and this one caps at "99+". */}{" "}
                    <Badge size="xs" className="tabular-nums">
                      {formatCount(openCount)}
                    </Badge>
                  </>
                )}
              </TabsTrigger>
              <TabsTrigger value="closed">
                {t("notifications.tabs.closed")}
              </TabsTrigger>
            </TabsList>
          </div>
          {stale && status === "ready" && (
            <div className="px-4 pt-2">
              <Alert
                tone="warning"
                actions={
                  <Button variant="outline" size="sm" onClick={onRetry}>
                    {t("notifications.error.retry")}
                  </Button>
                }
              >
                {t("notifications.error.stale")}
              </Alert>
            </div>
          )}
          {/* A tab switch remounts the panel, so the scroll position starts at the top. */}
          <TabsContent value={tab} className="relative">
            <div ref={scrollRef} className="max-h-[448px] overflow-y-auto">
              {body}
            </div>
            {/* Always mounted so a screen reader announces the text when it appears. */}
            <span role="status" className="sr-only">
              {hasNewEntries ? t("notifications.newEntriesStatus") : ""}
            </span>
            {hasNewEntries && (
              // Floats over the top of the list: its appearance moves no row.
              <div className="pointer-events-none absolute inset-x-0 top-2 z-20 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  leadingIcon={RefreshCw}
                  className="pointer-events-auto"
                  onClick={() => {
                    onShowNew?.();
                    if (scrollRef.current) scrollRef.current.scrollTop = 0;
                  }}
                >
                  {t("notifications.newEntries")}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

interface FuerDichListProps {
  open: boolean;
  openCount: number | null;
  onClose: () => void;
  /** Called after an action succeeded, so the bell's count is read again. */
  onCountStale?: () => void;
  /** Goes up whenever the server's list may have changed (push, poll, focus). */
  changeSignal?: number;
  titleId?: string;
}

/**
 * The list of the caller's "Für dich" entries behind the bell.
 *
 * What is rendered is a SNAPSHOT: it is replaced only when a load the user started
 * completes (opening the popover, switching the tab, retrying), never by a later cache
 * write, push or poll, so nothing reorders or disappears by itself (D-16). That is why the
 * entries are fetched with `client.query` into component state and not read through a
 * watching `useQuery`, which would follow every cache update (a mark-read result, say).
 *
 * The popover content unmounts on close, so every opening starts on Offen with a fresh load.
 */
export function FuerDichList({
  open,
  openCount,
  onClose,
  onCountStale,
  changeSignal = 0,
  titleId,
}: FuerDichListProps) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [markDone] = useMutation(FUER_DICH_MARK_DONE);
  const [reopen] = useMutation(FUER_DICH_REOPEN);
  const [confirmRequest] = useMutation(FUER_DICH_CONFIRM_REQUEST);
  const { pathname } = useLocation();
  const [tab, setTab] = useState<FuerDichTab>("open");
  const [reloads, setReloads] = useState(0);
  const [snapshot, setSnapshot] = useState<Snapshot>(LOADING);
  // The last list each tab showed, so a tab switch shows rows at once (no skeleton flash)
  // while the switch's own load refreshes them. Both tabs load when the popover opens.
  const tabCacheRef = useRef<Partial<Record<FuerDichTab, Snapshot>>>({});
  useEffect(() => {
    if (snapshot.status === "ready") tabCacheRef.current[tab] = snapshot;
  }, [snapshot, tab]);

  const prefetch = useCallback(
    (which: FuerDichTab) => {
      client
        .query({
          query: FUER_DICH_ENTRIES,
          variables: { closed: which === "closed", limit: PAGE_SIZE },
          fetchPolicy: "network-only",
        })
        .then(({ data }) => {
          if (!data) return;
          const entries = getFragmentData(
            FUER_DICH_ENTRY_FIELDS,
            data.notificationEntries,
          );
          tabCacheRef.current[which] = {
            status: "ready",
            entries,
            hasMore: entries.length >= PAGE_SIZE,
          };
        })
        // A failed look-ahead costs nothing: the switch loads the tab itself.
        .catch(() => undefined);
    },
    [client],
  );
  // A fresher first page the server has, held apart until the user asks for it.
  const [held, setHeld] = useState<{
    entries: FuerDichEntry[];
    hasMore: boolean;
  } | null>(null);
  const [stale, setStale] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Counts the loads the user started. An action that fails after the user switched tab must
  // not put its row into a list it never belonged to.
  const loadGenerationRef = useRef(0);
  const entriesRef = useRef<FuerDichEntry[]>([]);
  useEffect(() => {
    entriesRef.current = snapshot.entries;
  }, [snapshot.entries]);

  useEffect(() => {
    if (!open) return;
    // The effect re-runs when the tab or the retry counter changes; the cleanup drops the
    // answer of the load it replaces, so a slow answer for the tab the user left never lands.
    let stale = false;
    loadGenerationRef.current += 1;
    client
      .query({
        query: FUER_DICH_ENTRIES,
        variables: { closed: tab === "closed", limit: PAGE_SIZE },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (stale) return;
        if (!data) throw new Error("notificationEntries returned no data");
        const entries = getFragmentData(
          FUER_DICH_ENTRY_FIELDS,
          data.notificationEntries,
        );
        setSnapshot({
          status: "ready",
          entries,
          hasMore: entries.length >= PAGE_SIZE,
        });
      })
      .catch(() => {
        if (stale) return;
        setSnapshot({ status: "error", entries: [], hasMore: false });
      });
    const other: FuerDichTab = tab === "open" ? "closed" : "open";
    if (!tabCacheRef.current[other]) prefetch(other);
    return () => {
      stale = true;
    };
  }, [client, open, tab, reloads, prefetch]);

  // A push, poll or focus says the server's list may have changed. Look in the background
  // and only HOLD what comes back: the rendered rows never follow it (D-16).
  const lastSignalRef = useRef(changeSignal);
  const statusRef = useRef(snapshot.status);
  useEffect(() => {
    statusRef.current = snapshot.status;
  }, [snapshot.status]);
  useEffect(() => {
    if (changeSignal === lastSignalRef.current) return;
    lastSignalRef.current = changeSignal;
    if (!open || statusRef.current !== "ready") return;
    const generation = loadGenerationRef.current;
    client
      .query({
        query: FUER_DICH_ENTRIES,
        variables: { closed: tab === "closed", limit: PAGE_SIZE },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (generation !== loadGenerationRef.current) return;
        if (!data) throw new Error("notificationEntries returned no data");
        const fresh = getFragmentData(
          FUER_DICH_ENTRY_FIELDS,
          data.notificationEntries,
        );
        setStale(false);
        setHeld(
          differsFromSnapshot(fresh, entriesRef.current)
            ? { entries: fresh, hasMore: fresh.length >= PAGE_SIZE }
            : null,
        );
      })
      .catch(() => {
        if (generation !== loadGenerationRef.current) return;
        setStale(true);
      });
  }, [changeSignal, client, open, tab]);

  const handleShowNew = () => {
    if (!held) return;
    // Counts as a load the user started: a slower action that fails now must not put its
    // row into the new list.
    loadGenerationRef.current += 1;
    setSnapshot({ status: "ready", ...held });
    setHeld(null);
  };

  const handleLoadMore = () => {
    const last = entriesRef.current.at(-1);
    if (!last || loadingMore) return;
    const generation = loadGenerationRef.current;
    const closed = tab === "closed";
    setLoadingMore(true);
    client
      .query({
        query: FUER_DICH_ENTRIES,
        variables: {
          closed,
          limit: PAGE_SIZE,
          beforeAt: closed ? last.closedAt : last.lastEventAt,
          beforeId: last.id,
          // The request rank is part of the open tab's sort key only.
          ...(closed
            ? {}
            : { beforeRequest: last.kind === NotificationKind.Request }),
        },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (generation !== loadGenerationRef.current) return;
        if (!data) throw new Error("notificationEntries returned no data");
        const page = getFragmentData(
          FUER_DICH_ENTRY_FIELDS,
          data.notificationEntries,
        );
        setSnapshot((prev) => {
          const shown = new Set(prev.entries.map((e) => e.id));
          return {
            ...prev,
            entries: [...prev.entries, ...page.filter((e) => !shown.has(e.id))],
            hasMore: page.length >= PAGE_SIZE,
          };
        });
      })
      .catch(() => {
        if (generation === loadGenerationRef.current) {
          toast.error(t("notifications.toast.failed"));
        }
      })
      .finally(() => setLoadingMore(false));
  };

  const handleTabChange = (next: FuerDichTab) => {
    if (next === tab) return;
    setSnapshot(tabCacheRef.current[next] ?? LOADING);
    setHeld(null);
    setStale(false);
    setTab(next);
  };

  const handleRetry = () => {
    setSnapshot({ status: "loading", entries: [], hasMore: false });
    setHeld(null);
    setStale(false);
    setReloads((n) => n + 1);
  };

  // Per-entry action, optimistic: the row leaves at once and comes back at its former place
  // when the server says no. The server is the truth (T-01-18): its answer decides.
  const act = useCallback(
    async (
      entry: FuerDichEntry,
      run: () => Promise<FuerDichEntry | null | undefined>,
      // Whether the returned entry shows the action did not take effect.
      refused: (result: FuerDichEntry) => boolean,
      refusedToast?: () => void,
    ) => {
      const generation = loadGenerationRef.current;
      const index = entriesRef.current.findIndex((e) => e.id === entry.id);
      setSnapshot((prev) => ({
        ...prev,
        entries: prev.entries.filter((e) => e.id !== entry.id),
      }));
      const restore = () => {
        if (generation !== loadGenerationRef.current) return;
        setSnapshot((prev) => {
          if (prev.entries.some((e) => e.id === entry.id)) return prev;
          const at = Math.min(Math.max(index, 0), prev.entries.length);
          return {
            ...prev,
            entries: [
              ...prev.entries.slice(0, at),
              entry,
              ...prev.entries.slice(at),
            ],
          };
        });
      };
      let result: FuerDichEntry | null | undefined;
      try {
        result = await run();
      } catch {
        restore();
        toast.error(t("notifications.toast.failed"));
        return;
      }
      if (result && refused(result)) {
        restore();
        refusedToast?.();
      }
      // An entry already in the target state, or one that is gone, stays removed without a
      // word: this tab closed it as far as the user can tell.
      onCountStale?.();
      // The entry moved to the other tab: its remembered list is out of date.
      const other: FuerDichTab = tab === "open" ? "closed" : "open";
      delete tabCacheRef.current[other];
      prefetch(other);
    },
    [onCountStale, t, tab, prefetch],
  );

  const handleDone = useCallback(
    (entry: FuerDichEntry) =>
      void act(
        entry,
        async () => {
          const { data } = await markDone({
            variables: { notificationId: entry.id },
          });
          return data?.notificationMarkDone
            ? getFragmentData(FUER_DICH_ENTRY_FIELDS, data.notificationMarkDone)
            : null;
        },
        () => false,
      ),
    [act, markDone],
  );

  // "Bestätigen" keeps the row where it is: the request stays open, only its state changes.
  const handleConfirm = useCallback(
    async (entry: FuerDichEntry) => {
      const generation = loadGenerationRef.current;
      try {
        const { data } = await confirmRequest({
          variables: { notificationId: entry.id },
        });
        const result = data?.notificationConfirmRequest
          ? getFragmentData(
              FUER_DICH_ENTRY_FIELDS,
              data.notificationConfirmRequest,
            )
          : null;
        if (!result || generation !== loadGenerationRef.current) return;
        setSnapshot((prev) => ({
          ...prev,
          entries: prev.entries.map((e) => (e.id === result.id ? result : e)),
        }));
      } catch {
        toast.error(t("notifications.toast.failed"));
      }
    },
    [confirmRequest, t],
  );

  const handleReopen = useCallback(
    (entry: FuerDichEntry) =>
      void act(
        entry,
        async () => {
          const { data } = await reopen({
            variables: { notificationId: entry.id },
          });
          return data?.notificationReopen
            ? getFragmentData(FUER_DICH_ENTRY_FIELDS, data.notificationReopen)
            : null;
        },
        // The sibling path: another update on the ticket is open, it was marked unread and
        // this one stays closed.
        (result) => result.closedAt != null,
        () => toast.info(t("notifications.toast.reopenBlocked")),
      ),
    [act, reopen, t],
  );

  const handleSelect = useCallback(
    (entry: FuerDichEntry, event: React.MouseEvent<HTMLElement>) => {
      // Ctrl/Cmd/Shift/Alt-click opens the link in a new tab: leave the popover and the
      // entry as they are, the other tab's ticket view closes an update itself.
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }
      onClose();
      const failed = () => toast.error(t("notifications.toast.failed"));
      client
        .mutate({
          mutation: FUER_DICH_MARK_READ,
          variables: { notificationId: entry.id },
        })
        .catch(failed);
      // Already on that ticket: the link is a no-op navigation, so the ticket view's own
      // "opened" effect (keyed on the conversation id) will not fire again.
      if (pathname === `/agent/${entry.conversationId}`) {
        client
          .mutate({
            mutation: FUER_DICH_OPENED,
            variables: { conversationId: entry.conversationId },
          })
          .catch(failed);
      }
    },
    [client, onClose, pathname, t],
  );

  return (
    <FuerDichListView
      tab={tab}
      onTabChange={handleTabChange}
      openCount={openCount}
      entries={snapshot.entries}
      status={snapshot.status}
      onRetry={handleRetry}
      onSelect={handleSelect}
      onDone={handleDone}
      onConfirm={(entry) => void handleConfirm(entry)}
      onReopen={handleReopen}
      hasNewEntries={held != null}
      onShowNew={handleShowNew}
      stale={stale}
      hasMore={snapshot.hasMore}
      loadingMore={loadingMore}
      onLoadMore={handleLoadMore}
      titleId={titleId}
    />
  );
}
